import { useMemo } from 'react';
import { getTodayEndDate, getTodayStartDate } from '../Calendar/helpers';
import { formatDateRange } from '../helpers/formatDateRange';
import { DateTimeRange, TimePeriodUnit } from '../types';

export interface Preset {
  timeRange: DateTimeRange;
  dateRangeString: string;
  displayName: string;
  title: string;
  renderPreset?: () => React.ReactNode;
}

export function useDatePresets(
  skipQuickLookupOptions: boolean,
  quickOptionsFilters?: {
    skipYesterday?: boolean;
  },
): Preset[] {
  const { skipYesterday } = quickOptionsFilters || {};

  return useMemo(() => {
    const presets: Preset[] = [];

    if (!skipQuickLookupOptions) {
      return presets;
    }

    const startOfToday = getTodayStartDate();
    const endOfToday = getTodayEndDate();

    if (!skipYesterday) {
      // yesterday
      const yesterday = startOfToday.subtract(1, 'day');
      const yesterdayString = formatDateRange(yesterday, endOfToday);
      presets.push({
        timeRange: {
          type: 'since',
          startAfter: yesterday,
        },
        dateRangeString: yesterdayString,
        displayName: 'Yesterday',
        title: yesterdayString,
      });
    }

    // 7D
    const last7D = startOfToday.subtract(7, 'day');
    const last7DString = formatDateRange(last7D, endOfToday);
    presets.push({
      timeRange: {
        type: 'last',
        unit: TimePeriodUnit.Day,
        value: 7,
      },
      dateRangeString: last7DString,
      displayName: '7D',
      title: last7DString,
    });

    // 14D
    const last14D = startOfToday.subtract(14, 'day');
    const last14DString = formatDateRange(last14D, endOfToday);
    presets.push({
      timeRange: {
        type: 'last',
        unit: TimePeriodUnit.Day,
        value: 14,
      },
      dateRangeString: last14DString,
      displayName: '14D',
      title: last14DString,
    });

    // 4W
    const last4W = startOfToday.subtract(4, 'week');
    const last4WString = formatDateRange(
      last4W.startOf('week').tz(),
      endOfToday,
    );
    presets.push({
      timeRange: {
        type: 'last',
        unit: TimePeriodUnit.Week,
        value: 4,
      },
      dateRangeString: last4WString,
      displayName: '4W',
      title: last4WString,
    });

    // 3M
    const last3M = startOfToday.subtract(3, 'month');
    const last3MString = formatDateRange(
      last3M.startOf('month').tz(),
      endOfToday,
    );
    presets.push({
      timeRange: {
        type: 'last',
        unit: TimePeriodUnit.Month,
        value: 3,
      },
      dateRangeString: last3MString,
      displayName: '3M',
      title: last3MString,
    });

    // 6M
    const last6M = startOfToday.subtract(6, 'month');
    const last6MSring = formatDateRange(
      last6M.startOf('month').tz(),
      endOfToday,
    );
    presets.push({
      timeRange: {
        type: 'last',
        unit: TimePeriodUnit.Month,
        value: 6,
      },
      dateRangeString: last6MSring,
      displayName: '6M',
      title: last6MSring,
    });

    // 12M
    const last12M = startOfToday.subtract(12, 'month');
    const last12MSring = formatDateRange(
      last12M.startOf('month').tz(),
      endOfToday,
    );
    presets.push({
      timeRange: {
        type: 'last',
        unit: TimePeriodUnit.Month,
        value: 12,
      },
      dateRangeString: last12MSring,
      displayName: '12M',
      title: last12MSring,
    });

    return presets;
  }, [skipQuickLookupOptions, skipYesterday]);
}
